import storefrontApi from 'api/storefront-api'
import {
  type CreateOrderResponse,
  type IngridCreateSessionResponse,
  type NexiRetrievePaymentResponse,
  type Option,
  type OrderDTO,
  type PaymentProviderConfig,
  type StoreDTO,
  type StorefrontOrderLine
} from 'ecosystem'
import { ingridApi, transformOrderItemsToCreateOrderItems } from 'api'
import { nexiApi } from 'api/utils/nexi'

export const getServerSidePropsCheckoutPage = async ({
  searchParams,
  paymentProviderConfig
}: {
  searchParams?: Record<string, string>
  paymentProviderConfig?: PaymentProviderConfig
}): Promise<{
  store: StoreDTO
  initialData?: {
    orderLines?: StorefrontOrderLine[]
    orderResponse?: CreateOrderResponse
  }
}> => {
  const getIngridResponse = async (shippingId: string) => {
    const { data: ingridResponse } = await ingridApi.checkout.getSession(shippingId)

    return ingridResponse ? (ingridResponse as CreateOrderResponse['ingridResponse']) : null
  }

  const getStoreFrontOrderResponse = async (orderId?: string) => {
    if (orderId) {
      const { data: order } = await storefrontApi.orders.getStorefrontOrder({
        orderNumber: orderId
      })

      return order
    }

    return null
  }

  const getOrderResponse = async (): Promise<{
    order: Option<OrderDTO>
    nexiResponse?: Option<NexiRetrievePaymentResponse>
    ingridResponse?: Option<IngridCreateSessionResponse>
  }> => {
    let orderIdFromPaymentResponse: string | undefined = searchParams?.orderId
    let nexiResponse: Option<NexiRetrievePaymentResponse> = null

    if (searchParams?.paymentId) {
      switch (paymentProviderConfig?.providerName ?? 'KLARNA') {
        case 'KLARNA':
        case 'SVEA':
          break
        case 'NEXI':
          nexiResponse = await nexiApi.checkout
            .retrievePayment(searchParams.paymentId)
            .then((res) => res.data)

          if (nexiResponse) {
            orderIdFromPaymentResponse = nexiResponse.payment.orderDetails.reference
          }
          break

        case 'QLIRO':
        case 'BRIQPAY':
          break
        case 'LEDYER':
          // todo: add LEDYER if needed
          break
      }
    }

    const order = await getStoreFrontOrderResponse(orderIdFromPaymentResponse)
    let ingridResponse: Option<IngridCreateSessionResponse> | undefined

    if (order?.shippingId) {
      switch (order.shippingProvider ?? 'DEFAULT') {
        case 'INGRID':
          ingridResponse = await getIngridResponse(order.shippingId)
          break

        case 'NSHIFT':
        case 'POSTNORD':
        case 'DEFAULT':
          break
      }
    }

    return {
      order,
      ingridResponse,
      nexiResponse
    }
  }

  const [store, orderAndPayment] = await Promise.all([
    storefrontApi.store.getStore({ query: ['deliveries', 'services', 'id', 'pickups'] }),
    getOrderResponse()
  ])

  const { order, ingridResponse, nexiResponse } = orderAndPayment

  const orderResponse: Partial<CreateOrderResponse> = {}
  const initialData: {
    orderLines?: StorefrontOrderLine[]
    orderResponse?: CreateOrderResponse
  } = {}

  if (order) {
    orderResponse.storefrontOrderResponse = order
    initialData.orderLines = order.items?.map((item) => {
      return {
        product: item.product,
        quantity: item.quantity,
        originalPrice: item.originalUnitPrice,
        unitPrice: item.unitPrice,
        vatRate: item.vatRate,
        services: item.services ?? [],
        subItems: transformOrderItemsToCreateOrderItems(item.subItems ?? [])
      }
    })
  }

  if (ingridResponse) {
    orderResponse.ingridResponse = ingridResponse
  }

  if (nexiResponse) {
    orderResponse.nexiResponse = { paymentId: nexiResponse.payment.paymentId }
  }

  if (Object.values(orderResponse).length) {
    initialData.orderResponse = orderResponse as CreateOrderResponse
  }

  return {
    store,
    ...(Object.values(initialData).length ? { initialData } : {})
  }
}
